import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, FileDown, FileSpreadsheet } from 'lucide-react'
import {
  EXPORT_TTL_HOURS,
  REPORT_FORMATS,
  REPORT_KINDS,
  type ReportFormat,
  type ReportKind,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Empty,
  PageHeader,
  SegmentedPills,
  Select,
  Table,
  useAuth,
  usePermissions,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { when, type Competition } from '../../lib/competition-types'
import type { Paginated } from '../../lib/rbac-types'
import { saveFile } from '../../lib/documents'
import { useSelection } from '../../lib/selection'
import { REPORTS_KEY, useExports, type ReportExport } from '../../lib/reports'

const STATUS_VARIANT = {
  queued: 'info',
  running: 'info',
  done: 'success',
  failed: 'danger',
  expired: 'neutral',
} as const

/**
 * Exports (cahier §13.2, ADR-036) : CSV, Excel ou PDF, produits en tâche de
 * fond, colonnes limitées au besoin du rapport, téléchargeables 24 h par leur
 * demandeur. 🔒 Notes, paiements et résultats : exports sensibles journalisés.
 */
export function ExportsPage() {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const competitions = useQuery({
    queryKey: ['admin', 'competitions', 'exports'],
    queryFn: () =>
      session.api
        .get<Paginated<Competition>>('/admin/competitions', { query: { perPage: 100 } })
        .then((r) =>
          r.data.filter(
            (c) => c.status !== 'draft' && can('reports.export', { type: 'competition', id: c.id })
          )
        ),
  })
  const [chosen, setChosen] = useState('')
  const competitionId = chosen || competitions.data?.[0]?.id || ''
  const scope = { type: 'competition' as const, id: competitionId }
  const kinds = (Object.keys(REPORT_KINDS) as ReportKind[]).filter((k) =>
    can(REPORT_KINDS[k].permission, scope)
  )
  const [pickedKind, setKind] = useState<ReportKind | ''>('')
  const kind = pickedKind && kinds.includes(pickedKind) ? pickedKind : (kinds[0] ?? '')
  const def = kind ? REPORT_KINDS[kind] : null
  const [format, setFormat] = useState<ReportFormat>('xlsx')
  const [stageId, setStageId] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const stages = useSelection(competitionId, Boolean(def?.stage))
  const exports = useExports()

  const request = useMutation({
    mutationFn: () =>
      session.api.post<ReportExport>(`/admin/competitions/${competitionId}/report-exports`, {
        kind,
        format,
        stageId: def?.stage ? stageId : null,
      }),
    onSuccess: async (e) => {
      toast.success(e.status === 'done' ? 'Export prêt' : 'Export en préparation')
      setErrors({})
      await queryClient.invalidateQueries({ queryKey: [...REPORTS_KEY, 'exports'] })
    },
    onError: (error) => {
      setErrors(fieldErrors(error))
      toast.error(errorMessage(error))
    },
  })
  const download = useMutation({
    mutationFn: async (e: ReportExport) =>
      saveFile(
        await session.api.download(`/admin/report-exports/${e.id}/download`),
        e.fileName ?? `export.${e.format}`
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...REPORTS_KEY, 'exports'] }),
    onError: (error) => toast.error(errorMessage(error)),
  })
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (kind) request.mutate()
  }

  const columns: Column<ReportExport>[] = [
    { key: 'date', header: 'Demandé le', cell: (e) => when(e.createdAt) },
    {
      key: 'kind',
      header: 'Export',
      cell: (e) => (
        <span className="stack" style={{ gap: 2 }}>
          <strong>
            {e.kindLabel} · {e.formatLabel}
          </strong>
          <span className="muted">
            {e.competition}
            {e.stage ? ` — ${e.stage}` : ''}
            {e.rows !== null ? ` · ${e.rows} ligne(s)` : ''}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'État',
      cell: (e) => (
        <span className="stack" style={{ gap: 2 }}>
          <Badge variant={STATUS_VARIANT[e.status]}>{e.statusLabel}</Badge>
          {e.status === 'done' && <span className="muted">jusqu’au {when(e.expiresAt)}</span>}
          {e.error && <span className="field-error">{e.error}</span>}
        </span>
      ),
    },
    {
      key: 'download',
      header: <span className="sr-only">Télécharger</span>,
      align: 'end',
      cell: (e) =>
        e.status === 'done' && (
          <Button
            size="sm"
            variant="secondary"
            icon={<Download />}
            loading={download.isPending && download.variables?.id === e.id}
            onClick={() => download.mutate(e)}
          >
            Télécharger
          </Button>
        ),
    },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Pilotage"
        title="Exports"
        description={`Données d’un concours en CSV, Excel ou PDF, limitées au besoin du rapport. Chaque export est journalisé et reste téléchargeable ${EXPORT_TTL_HOURS} h par vous seul.`}
      />
      {competitions.isError && <Alert tone="danger">{errorMessage(competitions.error)}</Alert>}
      {competitions.data?.length === 0 && (
        <Alert tone="info">Aucun concours publié dans votre périmètre.</Alert>
      )}
      {competitionId && (
        <div className="template-layout">
          <Card>
            <CardHeader title="Nouvel export" />
            <CardBody>
              <form className="stack" onSubmit={submit} noValidate>
                <Select
                  label="Concours"
                  value={competitionId}
                  options={(competitions.data ?? []).map((c) => ({
                    value: c.id,
                    label: `${c.reference} — ${c.title}`,
                  }))}
                  onChange={(e) => {
                    setChosen(e.target.value)
                    setStageId('')
                  }}
                />
                {kinds.length === 0 ? (
                  <Alert tone="info">
                    Aucun export disponible : chaque nature demande sa propre permission
                    (candidatures, paiements ou résultats) sur ce concours.
                  </Alert>
                ) : (
                  <>
                    <Select
                      label="Données"
                      value={kind}
                      options={kinds.map((k) => ({ value: k, label: REPORT_KINDS[k].label }))}
                      error={errors.kind}
                      onChange={(e) => setKind(e.target.value as ReportKind)}
                    />
                    {def?.stage && (
                      <Select
                        label="Étape"
                        value={stageId}
                        placeholder="Choisir une étape"
                        options={(stages.data?.stages ?? []).map((s) => ({
                          value: s.id,
                          label: `${s.position}. ${s.label}`,
                        }))}
                        error={errors.stageId}
                        onChange={(e) => setStageId(e.target.value)}
                      />
                    )}
                    <div className="stack" style={{ gap: 'var(--space-2)' }}>
                      <span className="field-label" aria-hidden="true">
                        Format
                      </span>
                      <SegmentedPills
                        label="Format"
                        value={format}
                        onChange={setFormat}
                        options={(Object.keys(REPORT_FORMATS) as ReportFormat[]).map((value) => ({
                          value,
                          label: REPORT_FORMATS[value],
                        }))}
                      />
                    </div>
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      <Button
                        variant="primary"
                        type="submit"
                        icon={<FileDown />}
                        loading={request.isPending}
                        disabled={Boolean(def?.stage) && !stageId}
                      >
                        Lancer l’export
                      </Button>
                    </div>
                  </>
                )}
              </form>
            </CardBody>
          </Card>
          {def && (
            <aside
              className="stack template-preview"
              aria-label="Contenu de l’export"
              style={{ gap: 'var(--space-3)' }}
            >
              <h2 className="eyebrow">Contenu</h2>
              <p className="muted" style={{ margin: 0 }}>
                {def.description}
              </p>
              <ul className="checklist">
                {def.columns.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
              {def.sensitive && (
                <Alert tone="warning">
                  Export sensible : la demande et chaque téléchargement sont journalisés.
                </Alert>
              )}
            </aside>
          )}
        </div>
      )}
      <Card>
        <CardHeader title="Mes exports (30 derniers jours)" />
        <Table
          columns={columns}
          rows={exports.data ?? []}
          rowKey={(e) => e.id}
          loading={exports.isPending}
          caption="Mes exports"
          empty={
            <Empty
              icon={<FileSpreadsheet aria-hidden="true" />}
              title="Aucun export"
              description="Les exports que vous lancez apparaissent ici."
            />
          }
        />
      </Card>
    </>
  )
}
